import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Checkbox,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";
import { useWorkshopBlocker, FLOW } from "../../logic/useWorkshopBlocker";


type Option = {
  key: string;
  label_en: string;
  label_de: string;
  correct: boolean;
};

const OPTIONS: Option[] = [
  { key: "id", label_en: "ID / Passport", label_de: "Personalausweis / Reisepass", correct: true },
  { key: "photos", label_en: "Vacation photos", label_de: "Urlaubsfotos", correct: false },
  { key: "health", label_en: "Health insurance card", label_de: "Krankenversicherung / Gesundheitskarte", correct: true },
  { key: "jewelry", label_en: "Jewelry", label_de: "Schmuck", correct: false },
  { key: "bank", label_en: "Bank documents & access", label_de: "Bankunterlagen und Zugangsdaten", correct: true },
  { key: "school", label_en: "School certificates", label_de: "Schulzeugnisse", correct: false },
  { key: "usb", label_en: "USB / digital backup", label_de: "USB / digitale Sicherung", correct: true },
  { key: "books", label_en: "Books", label_de: "Bücher", correct: false },
  { key: "contacts", label_en: "Emergency contacts", label_de: "Notfallkontakte", correct: true },
  { key: "statements", label_en: "Bank statements", label_de: "Kontoauszüge", correct: false },
  { key: "contracts", label_en: "Important contracts", label_de: "Wichtige Verträge", correct: true },
  { key: "cash", label_en: "Large cash", label_de: "Bargeld (groß)", correct: false },
];

const STORAGE_PREFIX = "documentsScenario";
const CACHE_TTL = 1000 * 60 * 30; // 30 min session

export default function ScenarioSecuringDocuments(props: { handlePageChange: (p: string) => void }) {
  const { handlePageChange } = props;
  const blocker = useWorkshopBlocker();
  const { lang } = useI18n();
  const isDE = lang === "de";
  const [notice, setNotice] = React.useState<string | null>(null);

  const [selected, setSelected] = React.useState<string[]>([]);
  const [mode, setMode] = React.useState<"select" | "result">("select");
  const [workshopId, setWorkshopId] = React.useState<string | null>(null);
  const [employeeId, setEmployeeId] = React.useState<string | null>(null);

  /* ---------------- INIT ---------------- */
  
  const myIndex = FLOW.indexOf("ScenarioSecuringDocuments");
  const blockerIndex = FLOW.indexOf(blocker);
  const canContinue = blockerIndex > myIndex;
  
  function handleContinue() {
    if (canContinue) {
      handlePageChange(FLOW[myIndex + 1]);
      return;
    }
    setNotice("ENG / DE");
  }

  function handleRedo() {
    if (workshopId && employeeId) {
      localStorage.removeItem(`${STORAGE_PREFIX}_${workshopId}_${employeeId}`);
    }

    setSelected([]);
    setMode("select");
  }

  const loadLocal = React.useCallback(
    (workshopId: string, employeeId: string, backendKeys: string[]) => {
      const key = `${STORAGE_PREFIX}_${workshopId}_${employeeId}`;
      const saved = localStorage.getItem(key);

      // Backend is source of truth.
      // If backend has no answers, frontend cache must be removed.
      if (backendKeys.length === 0) {
        localStorage.removeItem(key);
        setSelected([]);
        setMode("select");
        return;
      }

      if (!saved) {
        setSelected(backendKeys);
        setMode("result");
        return;
      }

      try {
        const parsed = JSON.parse(saved);

        if (
          !parsed ||
          !Array.isArray(parsed.selected) ||
          typeof parsed.timestamp !== "number"
        ) {
          localStorage.removeItem(key);
          setSelected(backendKeys);
          setMode("result");
          return;
        }

        const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;

        if (isExpired) {
          localStorage.removeItem(key);
          setSelected(backendKeys);
          setMode("result");
          return;
        }

        // Only use cache if backend confirms answers exist.
        setSelected(parsed.selected);
        setMode("result");
      } catch (e) {
        console.error("LOCAL PARSE ERROR", e);
        localStorage.removeItem(key);
        setSelected(backendKeys);
        setMode("result");
      }
    },
    []
  );

  const loadContext = React.useCallback(async () => {
    const { data: userRes } = await supabase.auth.getUser();

    const { data: emp } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    const { data: workshop } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .single();

    if (!emp || !workshop) return;

    setEmployeeId(emp.id);
    setWorkshopId(workshop.id);

    const { data: existingRows, error } = await supabase
      .from("workshop_participant_documents")
      .select("document_key")
      .eq("workshop_id", workshop.id)
      .eq("employee_id", emp.id);

    if (error) {
      console.error("LOAD DOCUMENT ANSWERS ERROR:", error);
      return;
    }

    const backendKeys = (existingRows ?? []).map((row) => row.document_key);

    loadLocal(workshop.id, emp.id, backendKeys);
  }, [loadLocal]);

  React.useEffect(() => {
    loadContext();
  }, [loadContext]);

  /* ---------------- TOGGLE ---------------- */

  function toggle(key: string) {
    if (mode === "result") return;

    setSelected((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  }

  /* ---------------- SUBMIT ---------------- */

  async function handleSubmit() {
    if (!workshopId || !employeeId) return;

    const key = `${STORAGE_PREFIX}_${workshopId}_${employeeId}`;

    // store locally (UX persistence)
    localStorage.setItem(
      key,
      JSON.stringify({
        selected,
        timestamp: Date.now(),
      })
    );

    // overwrite DB (analytics only)
    await supabase
      .from("workshop_participant_documents")
      .delete()
      .eq("workshop_id", workshopId)
      .eq("employee_id", employeeId);

    const inserts = selected.map((key) => {
      const opt = OPTIONS.find((o) => o.key === key);
      return {
        workshop_id: workshopId,
        employee_id: employeeId,
        document_key: key,
        is_correct: opt?.correct ?? false,
      };
    });

    await supabase.from("workshop_participant_documents").insert(inserts);

    setMode("result");
  }

  /* ---------------- HELPERS ---------------- */

  function getColor(opt: Option) {
    if (mode === "select") return "rgba(255,255,255,0.2)";

    const isSelected = (selected ?? []).includes(opt.key);

    if (isSelected && opt.correct) return "#4caf50";
    if (isSelected && !opt.correct) return "#ff5252";
    if (!isSelected && opt.correct) return "#ffc107";

    return "rgba(255,255,255,0.1)";
  }

  function getIndicator(opt: Option) {
    if (mode === "select") return "";

    const isSelected = (selected ?? []).includes(opt.key);

    if (isSelected && opt.correct) return "✔";
    if (isSelected && !opt.correct) return "✖";
    if (!isSelected && opt.correct) return "!";

    return "";
  }

  /* ---------------- UI ---------------- */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/secureDocs.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
        py: { xs: 3, sm: 5 },
      }}
    >
      <Paper
        sx={{
          width: "min(900px, 100%)",
          maxWidth: 900,
          p: { xs: 2.5, sm: 4, md: 5 },
          borderRadius: { xs: 3, sm: 5 },
          background: "linear-gradient(180deg, #311f1f, #221515)",
        }}
      >
        <Stack spacing={{ xs: 3, sm: 4 }}>

          {/* TITLE */}
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "1.35rem", sm: "1.6rem", md: "1.8rem" } }}>
            {isDE
              ? "Du musst dein Zuhause verlassen - ohne zurückzukommen"
              : "You must leave your home - without returning"}
          </Typography>

          {/* SUB */}
          <Typography sx={{ color: "#aaa" }}>
            {isDE
              ? "👉 Du hast nur Platz für die wichtigsten Dokumente. Was nimmst du mit?"
              : "👉 You only have space for the most important documents. What do you take?"}
          </Typography>

          {/* OPTIONS */}
          <Stack>
            {OPTIONS.map((opt) => {
              const checked = (selected ?? []).includes(opt.key);

              return (
                <Box
                  key={opt.key}
                  sx={{
                    border: `1px solid ${getColor(opt)}`,
                    borderRadius: 2,
                    mb: 1,
                    px: 2,
                    py: 1.2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <Box
                    onClick={() => toggle(opt.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: mode === "result" ? "default" : "pointer",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={mode === "result"}
                      sx={{
                        color: "#888",
                        "&.Mui-checked": { color: "#ff4d4d" },
                      }}
                    />

                    <Typography sx={{ color: "#fff", fontWeight: 500 }}>
                      {isDE ? opt.label_de : opt.label_en}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 800,
                      color:
                        mode === "result"
                          ? (selected ?? []).includes(opt.key)
                            ? opt.correct
                              ? "#4caf50"
                              : "#ff5252"
                            : opt.correct
                            ? "#ffc107"
                            : "transparent"
                          : "transparent",
                    }}
                  >
                    {getIndicator(opt)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          {/* ACTION */}
          {mode === "select" ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={selected.length === 0}
              sx={{
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                fontWeight: 700,
                "&.Mui-disabled": {
                  background: "#777",
                  color: "rgba(255,255,255,0.75)",
                },
              }}
            >
              {isDE ? "Antwort prüfen" : "Check answers"}
            </Button>
          ) : (
            <Stack>
              <Button
                variant="outlined"
                onClick={handleRedo}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.4)",
                  fontWeight: 700,
                  "&:hover": {
                    borderColor: "#fff",
                    background: "rgba(255,255,255,0.05)",
                  },
                }}
              >
                {isDE ? "Antwort ändern" : "Change answer"}
              </Button>
              <Button
                variant="contained"
                onClick={handleContinue}
                sx={{
                  mt: 1,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 800,
                  px: 3,
                  bgcolor: canContinue ? "#ffffff" : "rgba(255,255,255,0.35)",
                  color: canContinue ? "#111827" : "rgba(255,255,255,0.8)",
                  "&:hover": {
                    bgcolor: canContinue ? "#f3f4f6" : "rgba(255,255,255,0.35)",
                  },
                }}
              >
                {isDE ? "Weiter" : "Continue"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setNotice(null)} severity="info" sx={{ width: "100%", fontWeight: 700 }}>
          {isDE ? "Die Workshop-Leitung wird diesen Schritt freigeben, oder bitte beantworte die Frage vollständig und sende sie zuerst ab." : "The workshop holder will enable this step, or please complete and submit your answer first."}
        </Alert>
      </Snackbar>
    </Box>
  );
}